import { defineStore } from 'pinia';
import { notify } from 'notiwind';
import { handleError } from '@/utils/error-handler';

export const useCreditScoreStore = defineStore('creditscore', {
    state: () => ({
        users: [],
        activeUser: {},
        activeScore: null,
        userScoreData: {
            latest_score: 0,
            prev_score_change_notation: '',
        },
        historyLineChart: [],
        tabState: '',
        showCreditScoreModal: false,
        showDeleteScoreModal: false,
        loadingResponse: false,
        loadingCreditScoreUsers: false,
        loadingUserCreditScores: false,
        loadingUserCreditScore: true,
        errors: [],
    }),
    actions: {
        setUsers() {
            this.loadingCreditScoreUsers = true;
            return ApiService.apiCall('/credit-scores/users', 'get')
                .then((response) => {
                    this.users = response.data.users;

                    setTimeout(() => {
                        this.loadingCreditScoreUsers = false;
                    }, 500);
                })
                .catch((response) => {
                    handleError(response);
                    this.loadingCreditScoreUsers = false;
                });
        },
        getUserScores() {
            // The page's activeUser watcher can fire before a member is picked.
            if (!this.activeUser?.id) {
                return Promise.resolve();
            }

            this.loadingUserCreditScores = true;
            this.loadingUserCreditScore = true;
            return ApiService.apiCall(
                '/credit-scores/users/' + this.activeUser.id,
                'get'
            )
                .then((response) => {
                    this.userScoreData = response.data;
                    this.tabState =
                        this.userScoreData.credit_scores.length > 0
                            ? 'analytics'
                            : 'start';
                    setTimeout(() => {
                        this.loadingUserCreditScores = false;
                        this.loadingUserCreditScore = false;
                    }, 500);
                })
                .catch((response) => {
                    handleError(response);
                    this.loadingUserCreditScores = false;
                    this.loadingUserCreditScore = false;
                });
        },
        getUserScore(user = null) {
            this.loadingUserCreditScore = true;
            return ApiService.apiCall('/credit-scores/user', 'post', user)
                .then((response) => {
                    this.userScoreData = response.data;
                    setTimeout(() => {
                        this.loadingUserCreditScore = false;
                    }, 500);
                })
                .catch((response) => {
                    handleError(response);
                    this.loadingUserCreditScore = false;
                });
        },
        getHistoryLineChart(period = null) {
            let url = '/credit-scores/users/' + this.activeUser.id + '/history-line-chart';

            if (period) {
                url += '?period=' + encodeURIComponent(period);
            }

            return ApiService.apiCall(url, 'get')
                .then((response) => {
                    this.historyLineChart = response.data;
                    return response;
                })
                .catch((error) => {
                    this.loadingUserCreditScore = false;
                    throw error;
                });
        },
        clearErrors() {
            this.errors = [];
        },
        setErrors(errors) {
            this.errors = errors;
        },
        async saveCreditScore(fields) {
            this.clearErrors();
            this.loadingResponse = true;
            let data = {
                user_id: this.activeUser.id,
                score: fields.score,
                score_date: fields.scoreDate,
                is_real_fico_score: fields.isRealFicoScore,
            };
            return ApiService.apiCall('/credit-scores/', 'post', data)
                .then(() => {
                    this.loadingResponse = false;
                    notify({
                        group: 'success',
                        title: 'Success',
                        text: 'Your credit score has been saved.',
                    });
                    if (import.meta.env.VITE_APP_ENV === 'production') {
                        woopra.track('creditscore-my10x', {
                            score: fields.score,
                            user_id: this.activeUser.id,
                            email: this.activeUser.email,
                            uuid: localStorage.getItem('uuid'),
                        });
                    }
                })
                .catch((error) => {
                    this.loadingResponse = false;
                    notify({
                        group: 'error',
                        title: 'Error',
                        text: 'There was an error saving your credit score. Please try again.',
                    });
                    throw error;
                });
        },
        setActiveUser(user) {
            this.activeUser = user ?? {};
        },
        setActiveScore(score) {
            this.activeScore = score;
        },
        setTabState(tabState) {
            if (tabState === 'cancelUpdate') {
                this.tabState =
                    this.userScoreData.credit_scores.length > 0
                        ? 'analytics'
                        : 'start';
            } else {
                this.tabState = tabState;
            }
        },
        async updateCreditScore(score) {
            this.clearErrors();
            this.loadingResponse = true;
            score.score_date = score.scoreDate;
            score.is_real_fico_score = score.isRealFicoScore;
            return ApiService.apiCall(
                '/credit-scores/' + score.uuid,
                'put',
                score
            )
                .then(() => {
                    this.loadingResponse = false;
                    if (import.meta.env.VITE_APP_ENV === 'production') {
                        woopra.track('creditscore-my10x', {
                            score: score.score,
                            user_id: this.activeUser.id,
                            email: this.activeUser.email,
                            uuid: localStorage.getItem('uuid'),
                        });
                    }
                    notify({
                        group: 'success',
                        title: 'Success',
                        text: 'Your credit score has been updated.',
                    });
                })
                .catch((error) => {
                    this.loadingResponse = false;
                    notify({
                        group: 'error',
                        title: 'Error',
                        text: 'There was an error updating your credit score. Please try again.',
                    });
                    throw error;
                });
        },
        deleteScore(score) {
            ApiService.apiCall('/credit-scores/' + score.uuid, 'delete')
                .then(() => {
                    this.loadingResponse = false;
                    this.userScoreData.all_scores.splice(
                        this.userScoreData.all_scores.indexOf(score),
                        1
                    );
                    notify({
                        group: 'success',
                        title: 'Success',
                        text: 'Your credit score has been deleted.',
                    });
                    this.getUserScores().catch((error) => {
                        console.error('Error refreshing scores after deletion:', error);
                    });
                })
                .catch((error) => {
                    this.loadingResponse = false;
                    notify({
                        group: 'error',
                        title: 'Error',
                        text: 'There was an error deleting your credit score. Please try again.',
                    });
                });
        },
        openCreditScoreModal() {
            this.showCreditScoreModal = true;
        },
        closeCreditScoreModal() {
            this.showCreditScoreModal = false;
            this.activeScore = null;
        },
        openDeleteScoreModal() {
            this.showDeleteScoreModal = true;
        },
        closeDeleteScoreModal() {
            this.showDeleteScoreModal = false;
        },
    },
});
